import { Box, Chip } from "@bca/ui-library";
import { SpaceProps } from "styled-system";
import { useRouter } from "next/router";
import addFiltersToPageUrl from "./addFiltersToPageUrl";
import getFilterLabel from "./getFilterLabel";

interface AppliedFiltersProps extends SpaceProps {
  clearButtonSlot?: JSX.Element;
  filterByCategoryKey?: string;
  appliedCheckboxes: any;
  setSelectedVehicles: any;
  setAppliedCheckboxes;
  allQueryStrings: any;
}

const getChipLabel = (filterKey: string, categoryKey?: string): string => {
  if (categoryKey?.toLowerCase() === "mileagewarranted")
    return "Warranted Mileage";
  if (categoryKey === "missingMileage") return "Mileage Known";
  if (categoryKey === "HasConditionReport") return "Condition Report";
  if (categoryKey?.toLowerCase() === "hasconditionreport")
    return "Condition Report";
  if (categoryKey?.toLowerCase() === "logbookstatus") return "V5 Present";
  if (categoryKey?.toLowerCase() === "hasbatteryhealthreport")
    return "Report Available";
  if (categoryKey === "awaitingAppraisal") return "Pricing Known";
  if (categoryKey === "EngineCapacity" && filterKey !== "None")
    return `${filterKey} Litres`;
  if (categoryKey === "NumberOfDoors" && filterKey !== "None")
    return filterKey !== "1" ? `${filterKey} Doors` : `${filterKey} Door`;

  const filter = getFilterLabel(categoryKey, filterKey);
  return filter.length > 27 ? `${filter.slice(0, 26)}...` : filter;
};

let timer;
const AppliedFilters: React.FunctionComponent<AppliedFiltersProps> = ({
  clearButtonSlot,
  filterByCategoryKey,
  appliedCheckboxes,
  setSelectedVehicles,
  setAppliedCheckboxes,
  allQueryStrings,
  ...props
}) => {
  const router = useRouter();
  const isMileageApplicable = filterByCategoryKey === "Mileage";
  const isGradeApplicable = filterByCategoryKey === "Grade";
  const isCAPCleanApplicable = filterByCategoryKey === "CapCleanPrice";

  function lowerCaseItem(arg: string) {
    return arg.toLocaleLowerCase();
  }

  const filteredList = filterByCategoryKey
    ? appliedCheckboxes?.filter(
        (item: { categoryKey: string }) =>
          lowerCaseItem(item?.categoryKey) ===
            lowerCaseItem(filterByCategoryKey) ||
          (isMileageApplicable &&
            (lowerCaseItem(item?.categoryKey) === "mileagewarranted" ||
              lowerCaseItem(item?.categoryKey) === "missingmileage")) ||
          (isGradeApplicable &&
            lowerCaseItem(item?.categoryKey) === "hasconditionreport") ||
          (isCAPCleanApplicable &&
            lowerCaseItem(item?.categoryKey) === "awaitingappraisal")
      )
    : appliedCheckboxes;

  const removeChip = (appliedCheckboxes, filterKey, categoryKey) => {
    setSelectedVehicles([]);
    let newState = null;
    const filterAlreadyApplied = appliedCheckboxes.find(
      (item) => item.filterKey === filterKey && item.categoryKey === categoryKey
    );
    const otherDependentFilters = appliedCheckboxes.filter(
      (item) =>
        item.parentFilterKey === categoryKey && item.groupedBy === filterKey
    );

    if (!filterAlreadyApplied && !otherDependentFilters) {
      return;
    }

    newState = [
      ...appliedCheckboxes.filter(
        (item) =>
          item !== filterAlreadyApplied &&
          !otherDependentFilters.find(
            (value) => value.filterKey === item.filterKey
          )
      ),
    ];
    setAppliedCheckboxes([...newState]);
    debounceUrlUpdate(newState);
  };

  const debounceUrlUpdate = (newState) => {
    clearTimeout(timer);

    timer = setTimeout(() => {
      const newUrl = addFiltersToPageUrl("bq", [...newState]);
      const currentPageUrl = new URL(window.location.href);
      if (newUrl.href === currentPageUrl.href) {
        return;
      }
      newUrl.searchParams.delete("page");
      newUrl.searchParams.delete("vrm");
      router.push(newUrl.href, newUrl.href, { shallow: true });
    }, 1000);
  };

  return (
    <>
      {filteredList?.length > 0 && (
        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          maxWidth="fit-content"
          {...props}
        >
          {filteredList?.map(({ categoryKey, filterKey }) => (
            <Chip
              key={categoryKey}
              label={getChipLabel(filterKey, categoryKey)}
              onRemove={() =>
                removeChip(appliedCheckboxes, filterKey, categoryKey)
              }
              mr={2}
              mt={2}
              css={`
                padding: 6px 8px;
                & span {
                  font-weight: 500;
                  font-size: 16px;
                }
              `}
            ></Chip>
          ))}
          {clearButtonSlot}
        </Box>
      )}
    </>
  );
};

export default AppliedFilters;
